import dayjs from 'dayjs'
import type { AlertColor } from '@mui/material'
import { ControlTowerCardStatus } from '../../../../../../pure-js/enums/ControlTowerCardStatus'
import { SprintActivityErrorCode } from '../../../../../../pure-js/enums/SprintActivityErrorCode'
import {
  countSprintCallMembersInRoom,
  type FacilitatorLiveSessionCard,
  type FacilitatorLiveSessionLoopSummary,
  type FacilitatorLiveSessionParticipant,
  type FacilitatorLiveSessionPhase,
  type FacilitatorSprintCallCard
} from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { getSprintCallTitle, type TranslateFunction } from '../../../shared/helpers/liveActivityHelpers'
import { GroupTutorialStatus, type GroupTutorialSummary } from '../../../../../../pure-js/types/GroupTutorial'
import { INTERAX_COLORS } from '../../../../constants/colors'

export type PillColors = { color: string; background: string }

/** What a card action came to, shown in an alert on the card until dismissed. */
export type ControlTowerActionOutcome = { severity: AlertColor; message: string }

const TIME_FORMAT = 'HH:mm'
const DATE_TIME_FORMAT = 'D MMM HH:mm'
const AVATAR_COLORS = [INTERAX_COLORS.blue, INTERAX_COLORS.green, INTERAX_COLORS.sun, INTERAX_COLORS.redSoft]
const INITIALS_MAX_LENGTH = 2

const getGameCardStatus = (card: FacilitatorLiveSessionCard): ControlTowerCardStatus => {
  if (card.currentLoopNumber && card.currentPhase) return ControlTowerCardStatus.LIVE
  if (card.groupTutorial?.status === GroupTutorialStatus.IN_PROGRESS) {
    return card.groupTutorial.isOverdue ? ControlTowerCardStatus.STUCK : ControlTowerCardStatus.LIVE
  }
  // Tutorial done and the first loop not written yet: the team is playing, not "not started".
  if (card.groupTutorial?.status === GroupTutorialStatus.COMPLETED) return ControlTowerCardStatus.LIVE
  return card.isDelayed ? ControlTowerCardStatus.DELAYED : ControlTowerCardStatus.NOT_STARTED
}

const getDebriefCardStatus = (card: FacilitatorLiveSessionCard): ControlTowerCardStatus => {
  switch (card.aiCoachStatus) {
    case 'ai_speaking':
    case 'paused':
      return ControlTowerCardStatus.LIVE
    case 'available':
      return card.isDelayed ? ControlTowerCardStatus.DELAYED : ControlTowerCardStatus.READY
    case 'pregenerating':
      return ControlTowerCardStatus.PREGENERATING
    case 'error':
      return ControlTowerCardStatus.ERROR
    default:
      return card.isDelayed ? ControlTowerCardStatus.DELAYED : ControlTowerCardStatus.NOT_STARTED
  }
}

export const getCardStatus = (card: FacilitatorLiveSessionCard): ControlTowerCardStatus =>
  card.kind === 'game' ? getGameCardStatus(card) : getDebriefCardStatus(card)

export const getStatusLabel = (status: ControlTowerCardStatus, t: TranslateFunction): string => {
  const labels: Record<ControlTowerCardStatus, string> = {
    [ControlTowerCardStatus.LIVE]: t('facilitator.controlTower.statusLive', 'Live'),
    [ControlTowerCardStatus.READY]: t('facilitator.controlTower.statusReady', 'Ready'),
    [ControlTowerCardStatus.PREGENERATING]: t('facilitator.controlTower.statusPregenerating', 'Pregenerating'),
    [ControlTowerCardStatus.DELAYED]: t('facilitator.controlTower.statusDelayed', 'Delayed'),
    [ControlTowerCardStatus.STUCK]: t('facilitator.controlTower.statusStuck', 'Stuck'),
    [ControlTowerCardStatus.ERROR]: t('facilitator.controlTower.statusError', 'Error'),
    [ControlTowerCardStatus.NOT_STARTED]: t('facilitator.controlTower.statusNotStarted', 'Not started')
  }
  return labels[status]
}

const LIVE_COLORS: PillColors = { color: INTERAX_COLORS.green, background: INTERAX_COLORS.greenBackground }
const ATTENTION_COLORS: PillColors = { color: INTERAX_COLORS.redError, background: INTERAX_COLORS.redErrorBackground }
const IDLE_COLORS: PillColors = { color: INTERAX_COLORS.thunderGrey, background: INTERAX_COLORS.white10 }

export const getStatusColors = (status: ControlTowerCardStatus): PillColors => {
  const colors: Record<ControlTowerCardStatus, PillColors> = {
    [ControlTowerCardStatus.LIVE]: LIVE_COLORS,
    [ControlTowerCardStatus.READY]: { color: INTERAX_COLORS.sun, background: INTERAX_COLORS.sun20 },
    [ControlTowerCardStatus.PREGENERATING]: { color: INTERAX_COLORS.blue, background: INTERAX_COLORS.blueBackground },
    [ControlTowerCardStatus.DELAYED]: ATTENTION_COLORS,
    [ControlTowerCardStatus.STUCK]: ATTENTION_COLORS,
    [ControlTowerCardStatus.ERROR]: ATTENTION_COLORS,
    [ControlTowerCardStatus.NOT_STARTED]: IDLE_COLORS
  }
  return colors[status]
}

// Someone in the room makes a sprint call live; until then it has not started.
export const getSprintCallStatus = (sprintCall: FacilitatorSprintCallCard): ControlTowerCardStatus =>
  countSprintCallMembersInRoom(sprintCall) > 0 ? ControlTowerCardStatus.LIVE : ControlTowerCardStatus.NOT_STARTED

// "3 / 48": who of the sprint is in its room, out of everyone in it.
export const formatSprintCallPresence = (sprintCall: FacilitatorSprintCallCard): string =>
  `${countSprintCallMembersInRoom(sprintCall)} / ${sprintCall.participants.length}`

// Why a call could not be opened: the team has no room yet, or anything else.
export const describeJoinCallFailure = (code: string | undefined, t: TranslateFunction): string =>
  code === SprintActivityErrorCode.CALL_ROOM_NOT_FOUND
    ? t('facilitator.controlTower.joinCallNoRoom', 'The team has no call room yet.')
    : t('facilitator.controlTower.joinCallFailed', 'Could not open the call.')

// Sends the tab opened on the click to the call, cut off from this page; a blocked tab opens a fresh one instead.
export const showCallInTab = (tab: Window | null, roomUrl: string): void => {
  if (!tab) {
    window.open(roomUrl, '_blank', 'noopener')
    return
  }

  tab.location.href = roomUrl
  tab.opener = null
}

export const getSprintCallKindLabel = (sprintCall: FacilitatorSprintCallCard, t: TranslateFunction): string =>
  getSprintCallTitle(sprintCall.activityType, t) || sprintCall.activityTitle

// The people a facilitator still waits for come first, everyone by name within.
export const orderMissingFirst = (
  participants: FacilitatorLiveSessionParticipant[]
): FacilitatorLiveSessionParticipant[] =>
  [...participants].sort(
    (first, second) => Number(first.inMeeting) - Number(second.inMeeting) || first.name.localeCompare(second.name)
  )

export const getKindLabel = (card: FacilitatorLiveSessionCard, t: TranslateFunction): string =>
  card.kind === 'game'
    ? t('facilitator.controlTower.kindGame', 'Game')
    : t('facilitator.controlTower.kindAiDebrief', 'AI Debrief')

export const formatPhaseLabel = (phase: FacilitatorLiveSessionPhase, t: TranslateFunction): string => {
  const labels: Record<FacilitatorLiveSessionPhase, string> = {
    planning: t('facilitator.controlTower.phasePlanning', 'Planning'),
    execution: t('facilitator.controlTower.phaseExecution', 'Execution'),
    reflection: t('facilitator.controlTower.phaseReflection', 'Reflection')
  }
  return labels[phase]
}

// The group tutorial owns the whole window before the first loop exists, so it is what the stage must say.
const formatGroupTutorialStage = (groupTutorial: GroupTutorialSummary, t: TranslateFunction): string =>
  groupTutorial.status === GroupTutorialStatus.IN_PROGRESS
    ? t('facilitator.controlTower.groupTutorialStage', 'Group tutorial • {{minutes}} min', {
        minutes: groupTutorial.minutes ?? 0
      })
    : t('facilitator.controlTower.groupTutorialStageDone', 'Group tutorial done • starting first loop')

export const formatStageText = (card: FacilitatorLiveSessionCard, t: TranslateFunction): string => {
  if (card.kind !== 'game') return card.stageLabel

  if (!card.currentLoopNumber || !card.currentPhase) {
    if (card.groupTutorial) return formatGroupTutorialStage(card.groupTutorial, t)
    return card.isDelayed
      ? t('facilitator.controlTower.notStartedDelayed', 'Not started • Delayed')
      : t('facilitator.controlTower.statusNotStarted', 'Not started')
  }

  const loopLabel = t('facilitator.controlTower.loopStage', 'Loop {{current}}/{{total}}', {
    current: card.currentLoopNumber,
    total: card.totalLoops ?? card.currentLoopNumber
  })
  const minuteLabel = card.minuteInLoop ? ` • ${card.minuteInLoop} ${t('common.minutesShort', 'min')}` : ''
  return `${loopLabel} • ${formatPhaseLabel(card.currentPhase, t)}${minuteLabel}`
}

// The activity's scheduled window in the facilitator's local time; the end drops its date when it is the same day.
export const formatScheduledWindow = (card: Pick<FacilitatorLiveSessionCard, 'startDate' | 'endDate'>): string => {
  const start = dayjs(card.startDate)
  const end = dayjs(card.endDate)
  if (!start.isValid() || !end.isValid()) return '—'
  const endFormat = start.isSame(end, 'day') ? TIME_FORMAT : DATE_TIME_FORMAT
  return `${start.format(DATE_TIME_FORMAT)} – ${end.format(endFormat)}`
}

export const formatLoopTimes = (loop: FacilitatorLiveSessionLoopSummary): string | null => {
  const started = loop.startedAt ? dayjs(loop.startedAt) : null
  if (!started?.isValid()) return null
  const finished = loop.finishedAt ? dayjs(loop.finishedAt) : null
  const finishedLabel = finished?.isValid() ? ` – ${finished.format(TIME_FORMAT)}` : ''
  return `${started.format(TIME_FORMAT)}${finishedLabel}`
}

export const getLoopStatusLabel = (loop: FacilitatorLiveSessionLoopSummary, t: TranslateFunction): string => {
  switch (loop.status) {
    case 'in_progress':
      return t('facilitator.controlTower.loopStatusLive', 'Live')
    case 'finished':
      return t('facilitator.controlTower.loopStatusDone', 'Done')
    default:
      return t('facilitator.controlTower.loopStatusPending', 'Pending')
  }
}

export const getLoopStatusColors = (loop: FacilitatorLiveSessionLoopSummary): PillColors => {
  switch (loop.status) {
    case 'in_progress':
      return LIVE_COLORS
    case 'finished':
      return { color: INTERAX_COLORS.white, background: INTERAX_COLORS.white10 }
    default:
      return { color: INTERAX_COLORS.thunderGrey, background: INTERAX_COLORS.white04 }
  }
}

export const formatLoopSummaryStage = (loop: FacilitatorLiveSessionLoopSummary, t: TranslateFunction): string => {
  const minuteLabel = loop.minuteInLoop ? ` • ${loop.minuteInLoop} ${t('common.minutesShort', 'min')}` : ''
  return `${formatPhaseLabel(loop.currentPhase, t)}${minuteLabel}`
}

export const formatLoopSummaryPresence = (loop: FacilitatorLiveSessionLoopSummary, t: TranslateFunction): string =>
  loop.participantNames.length
    ? t('facilitator.controlTower.activelyPlayed', 'Actively played: {{names}}', {
        names: loop.participantNames.join(', ')
      })
    : t('facilitator.controlTower.activelyPlayedNobody', 'Actively played: nobody yet')

// First letter of the first two words; a one-word name (a nickname) lends its first two letters instead.
export const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters =
    words.length > 1
      ? words.slice(0, INITIALS_MAX_LENGTH).map((word) => word.charAt(0))
      : [words[0]?.slice(0, INITIALS_MAX_LENGTH) ?? '']
  return letters.join('').toUpperCase()
}

// A stable colour per name, so the same person keeps the same avatar across refreshes and cards.
export const getAvatarColor = (name: string): string => {
  const hash = Array.from(name).reduce((total, character) => total + character.charCodeAt(0), 0)
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}
